/**
 * Service layer — "Progress Gate" (Round 17).
 *
 * Weekly enforcement that a project's Client Manager keeps Percent Complete Override current on
 * their active Fixed-Fee phases. Deliberately separate from services/timeGate.js — different
 * responsibility model and cadence (see docs/PROGRESS_GATE.md). All calls go through the
 * Clerk-authed api/progress-gate.js — no browser secret, no direct Supabase/Airtable access.
 *
 *   getProgressGateStatus(memberId)   → { gated, staleCount, phases:[...], reason }
 *   confirmPhaseProgress(phaseId, pct)→ bump the confirmation timestamp (call on every Save,
 *                                       changed or not — see the note in the function itself)
 *   snoozeProgressGate(memberId, hrs) → personal, time-boxed snooze (no daily cap — see the doc)
 *   pauseProgressGate(hours)          → admin: auto-expiring firm-wide pause
 *   getProgressGateSettings()         → admin: { enabled, paused_until, paused_by } — cadence is
 *                                       fixed (weekly, reset Monday America/Los_Angeles), not
 *                                       configurable; see api/progress-gate.js
 *   setProgressGateSettings(fields)   → admin
 *   getClientManagerRoster()          → admin: [{ projectUid, projectName, managerId }] every
 *                                       active project — managerId is null when unresolved. Powers
 *                                       both backfilling and later reassignment (setClientManager
 *                                       has no lock; use it again to change an existing manager).
 */

async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

async function gatePost(body) {
  const token = await authToken()
  const res = await fetch('/api/progress-gate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function gateGet(qs) {
  const token = await authToken()
  const res = await fetch('/api/progress-gate?' + qs, { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}

export async function getProgressGateStatus(memberId) {
  if (!memberId) return { gated: false }
  return gateGet('action=status&memberId=' + encodeURIComponent(memberId))
}
/** Of these milestone ids, the ones still unposted. Returns null when it couldn't tell. */
export async function stillOpenMilestones(ids) {
  if (!ids || !ids.length) return []
  const d = await gateGet('action=openCheck&ids=' + encodeURIComponent(ids.join(',')))
  return Array.isArray(d.open) ? d.open : null
}
export function snoozeProgressGate(memberId, hours) {
  return gatePost({ action: 'snooze', memberId, hours })
}
// Bump the confirmation timestamp on every Save click, whether or not the percent actually
// changed — Airtable's own `Percent Complete Updated` only ticks on a real value change, so
// re-saving the same number ("confirmed, no change") never bumped it and the phase kept
// re-appearing after refresh. This is the fix — see docs/PROGRESS_GATE.md.
export function confirmPhaseProgress(phaseId, pctComplete) {
  return gatePost({ action: 'confirmPhase', phaseId, pctComplete })
}
export function pauseProgressGate(hours) {
  return gatePost({ action: 'pauseEnforcement', hours })
}
export async function getProgressGateSettings() {
  const d = await gateGet('action=settings')
  return d.settings || null
}
export function setProgressGateSettings(fields) {
  return gatePost({ action: 'setSettings', ...fields })
}
export async function getClientManagerRoster() {
  const d = await gateGet('action=clientManagerRoster')
  return d.projects || []
}
